import React, { useState, useEffect } from "react";
import { 
  listConsultorios, 
  createConsultorio, 
  updateConsultorio 
} from "../api/odontologia";
import { showAlertError, showAlertExito } from "../utils/alertHelper";
import Loader from "./Loader";
import { 
  FiActivity, FiPlus, FiUser, 
  FiCheckCircle, FiClock, FiEdit3
} from "react-icons/fi";

const Consultorios = () => {
  const [loading, setLoading] = useState(true);
  const [consultorios, setConsultorios] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [form, setForm] = useState({ nombre: "", status: 1 });
  
  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await listConsultorios();
      setConsultorios(res?.data || []);
    } catch (error) {
      console.error("Error al cargar consultorios:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleOpenModal = (consultorio = null) => {
    if (consultorio) {
      setForm({ 
        nombre: consultorio.nombre, 
        status: consultorio.status
      });
      setCurrentId(consultorio.id);
      setEditMode(true);
    } else {
      setForm({ nombre: "", status: 1 });
      setEditMode(false);
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editMode) {
        await updateConsultorio(currentId, form);
      } else {
        await createConsultorio(form);
      }
      setShowModal(false);
      fetchData();
    } catch (error) {
      showAlertError("Error", "No se pudo guardar el consultorio.");
    }
  };

  if (loading && consultorios.length === 0) return <Loader />;

  return (
    <div className="flex flex-col h-full bg-slate-50 overflow-hidden animate-fade-in">
      
      {/* HEADER */}
      <div className="bg-white border-b border-slate-200 px-6 py-5 flex items-center justify-between shrink-0 shadow-sm">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-blue-600 flex items-center justify-center text-white shadow-xl shadow-blue-100">
            <FiActivity size={24} />
          </div>
          <div>
            <h2 className="text-2xl font-black text-slate-800 leading-none tracking-tight">Gestión de Consultorios</h2>
            <p className="text-sm text-slate-400 mt-1 font-medium">Administración de consultorios odontológicos</p>
          </div>
        </div>
        <button 
          onClick={() => handleOpenModal()}
          className="flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-2xl hover:bg-blue-700 transition-all font-bold shadow-lg shadow-blue-200 active:scale-95"
        >
          <FiPlus size={20} /> Nuevo Consultorio
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
        
        {/* RESUMEN DE ESTADOS */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
           <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
                <FiCheckCircle size={28} />
              </div>
              <div>
                <p className="text-xs font-black uppercase text-slate-400 tracking-widest mb-1">Disponibles</p>
                <h4 className="text-3xl font-black text-slate-800">{consultorios.filter(c => c.status === 1).length}</h4>
              </div>
           </div>
           <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center">
                <FiClock size={28} />
              </div>
              <div>
                <p className="text-xs font-black uppercase text-slate-400 tracking-widest mb-1">Ocupados</p>
                <h4 className="text-3xl font-black text-slate-800">{consultorios.filter(c => c.status === 2).length}</h4>
              </div>
           </div>
           <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center">
                <FiActivity size={28} />
              </div>
              <div>
                <p className="text-xs font-black uppercase text-slate-400 tracking-widest mb-1">Total</p>
                <h4 className="text-3xl font-black text-slate-800">{consultorios.length}</h4>
              </div>
           </div>
        </div>

        {/* TABLERO DE CONSULTORIOS */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {consultorios.map((c) => {
            const isOccupied = c.status === 2;
            return (
              <div
                key={c.id}
                className={`group relative perspective bg-white rounded-[2.5rem] p-6 border-2 transition-all duration-500 hover:shadow-2xl flex flex-col items-center justify-center
                  ${isOccupied 
                    ? "border-rose-100 shadow-rose-50/50" 
                    : "border-emerald-100 shadow-emerald-50/50"
                  }`}
              >
                {/* Indicador de Estado Flotante */}
                <div className={`absolute top-4 left-4 flex items-center gap-1.5 px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-tighter shadow-sm
                  ${isOccupied ? "bg-rose-100 text-rose-600" : "bg-emerald-100 text-emerald-600"}`}>
                  <div className={`w-1.5 h-1.5 rounded-full ${isOccupied ? "bg-rose-500 animate-pulse" : "bg-emerald-500"}`}></div>
                  {isOccupied ? "Ocupado" : "Libre"}
                </div>

                {/* Acciones de Edición */}
                <button 
                  onClick={() => handleOpenModal(c)}
                  className="absolute top-4 right-4 p-2 rounded-xl bg-slate-50 text-slate-300 hover:bg-blue-50 hover:text-blue-600 transition-all opacity-0 group-hover:opacity-100"
                >
                  <FiEdit3 size={14} />
                </button>

                <div className={`w-20 h-20 rounded-3xl flex items-center justify-center mb-4 transition-all duration-500 transform group-hover:scale-110
                  ${isOccupied ? "bg-rose-50 text-rose-600" : "bg-emerald-50 text-emerald-600"}`}
                >
                  <span className="text-xl font-black text-center px-2">{c.nombre}</span>
                </div>

                <div className="text-center mb-6">
                </div>

                {/* BOTÓN DE ESTADO (Visual) */}
                <div
                  className={`w-full py-3 rounded-2xl font-black text-xs uppercase text-center tracking-widest transition-all
                    ${isOccupied 
                      ? "bg-rose-50 text-rose-600" 
                      : "bg-emerald-50 text-emerald-600"
                    }`}
                >
                  {isOccupied ? "En Atención" : "Disponible"}
                </div>
              </div>
            );
          })}
          {consultorios.length === 0 && !loading && (
             <div className="col-span-full flex flex-col items-center justify-center py-20 bg-white rounded-[2.5rem] border-2 border-dashed border-slate-200">
                <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center text-slate-300 mb-4">
                   <FiActivity size={32} />
                </div>
                <h3 className="text-lg font-bold text-slate-800">No hay consultorios registrados</h3>
                <p className="text-slate-400 text-sm">Comienza agregando uno nuevo.</p>
             </div>
          )}
        </div>
      </div>

      {/* MODAL ADMIN */}
      {showModal && (
        <div className="fixed inset-0 z-[100] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
           <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden animate-zoom-in">
              <div className="p-6 border-b border-slate-50 flex justify-between items-center bg-blue-600 text-white">
                 <h4 className="font-extrabold text-lg flex items-center gap-2">
                   {editMode ? <FiEdit3 /> : <FiPlus />}
                   {editMode ? "Editar Consultorio" : "Nuevo Consultorio"}
                 </h4>
                 <button onClick={() => setShowModal(false)} className="hover:rotate-90 transition-transform"><FiPlus className="rotate-45" size={24} /></button>
              </div>
              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                 <div>
                    <label className="block text-xs font-black uppercase text-slate-400 mb-2">Nombre del Consultorio</label>
                    <input 
                      type="text" 
                      required
                      placeholder="Ej: Consultorio 1"
                      value={form.nombre}
                      onChange={(e) => setForm({...form, nombre: e.target.value})}
                      className="w-full bg-slate-50 border-2 border-slate-100 rounded-xl px-4 py-3 focus:border-blue-400 outline-none transition-all font-bold"
                    />
                 </div>

                 
                 {editMode && (
                   <div>
                    <label className="block text-xs font-black uppercase text-slate-400 mb-2">Estado</label>
                    <select 
                      value={form.status}
                      onChange={(e) => setForm({...form, status: Number(e.target.value)})}
                      className="w-full bg-slate-50 border-2 border-slate-100 rounded-xl px-4 py-3 focus:border-blue-400 outline-none transition-all font-bold"
                    >
                      <option value={1}>Disponible</option>
                      <option value={2}>Ocupado</option>
                    </select>
                   </div>
                 )}

                 <button type="submit" className="w-full bg-blue-600 text-white py-4 rounded-2xl font-black shadow-lg shadow-blue-100 hover:bg-blue-700 transition-all mt-4">
                    {editMode ? "Actualizar" : "Registrar"}
                 </button>
              </form>
           </div>
        </div>
      )}

    </div>
  );
};

export default Consultorios;
